import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import './Export.css';

interface ExportOptions {
  fileFormat: 'SVG' | 'EPS' | 'PDF' | 'DXF' | 'PNG';
  svgVersion: '1.0' | '1.1' | 'tiny-1.2';
  fixedSize: boolean;
  adobeCompatibility: boolean;
  dxfCompatibility: 'lines-only' | 'lines-arcs' | 'lines-arcs-splines';
  drawStyle: 'fill' | 'stroke-outline' | 'stroke-edges';
  shapeStacking: 'cutouts' | 'stacked';
  groupBy: 'none' | 'color' | 'parent' | 'layer';
  parameterizedShapes: boolean;
  allowedCurveTypes: {
    lines: boolean;
    quadratic: boolean;
    cubic: boolean;
    circular: boolean;
    elliptical: boolean;
  };
  lineFitTolerance: 'coarse' | 'medium' | 'fine' | 'super-fine';
  fillGaps: boolean;
  clipOverflow: boolean;
  nonScalingStroke: boolean;
  strokeWidth: number;
  strokeStyle: {
    nonScaling: boolean;
    overrideColor: boolean;
    fullColor: boolean;
  };
  strokeStyleWidth: number;
}

const Export: React.FC = () => {
  const [vectorizedSvg, setVectorizedSvg] = useState<string>('');
  const navigate = useNavigate();

  const [options, setOptions] = useState<ExportOptions>({
    fileFormat: 'SVG',
    svgVersion: '1.1',
    fixedSize: false,
    adobeCompatibility: false,
    dxfCompatibility: 'lines-arcs',
    drawStyle: 'fill',
    shapeStacking: 'cutouts',
    groupBy: 'none',
    parameterizedShapes: false,
    allowedCurveTypes: {
      lines: true,
      quadratic: true,
      cubic: true,
      circular: true,
      elliptical: true
    },
    lineFitTolerance: 'medium',
    fillGaps: true,
    clipOverflow: false,
    nonScalingStroke: true,
    strokeWidth: 2.0,
    strokeStyle: {
      nonScaling: true,
      overrideColor: false,
      fullColor: true
    },
    strokeStyleWidth: 1.0
  });

  useEffect(() => {
    const svg = localStorage.getItem('vectorizedSvg');
    if (!svg) {
      navigate('/');
      return;
    }
    setVectorizedSvg(svg);
  }, [navigate]);

  const handleDownload = () => {
    const blob = new Blob([vectorizedSvg], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `vectorized.${options.fileFormat.toLowerCase()}`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const handleReset = () => {
    setOptions({
      fileFormat: 'SVG',
      svgVersion: '1.1',
      fixedSize: false,
      adobeCompatibility: false,
      dxfCompatibility: 'lines-arcs',
      drawStyle: 'fill',
      shapeStacking: 'cutouts',
      groupBy: 'none',
      parameterizedShapes: false,
      allowedCurveTypes: {
        lines: true,
        quadratic: true,
        cubic: true,
        circular: true,
        elliptical: true
      },
      lineFitTolerance: 'medium',
      fillGaps: true,
      clipOverflow: false,
      nonScalingStroke: true,
      strokeWidth: 2.0,
      strokeStyle: {
        nonScaling: true,
        overrideColor: false,
        fullColor: true
      },
      strokeStyleWidth: 1.0
    });
  };

  return (
    <div className="export-container">
      <div className="export-options-panel">
        <div className="option-section">
          <div className="section-title">
            File Format <span className="info-icon">ⓘ</span>
          </div>
          <div className="radio-group">
            {['SVG', 'EPS', 'PDF', 'DXF', 'PNG'].map((format) => (
              <label key={format} className="radio-label">
                <input
                  type="radio"
                  name="fileFormat"
                  value={format}
                  checked={options.fileFormat === format}
                  onChange={(e) => setOptions({...options, fileFormat: e.target.value as any})}
                />
                {format}
              </label>
            ))}
          </div>
        </div>

        <div className="option-section">
          <div className="section-title">
            SVG Version <span className="info-icon">ⓘ</span>
          </div>
          <div className="radio-group">
            {[{value: '1.0', label: 'SVG 1.0'}, {value: '1.1', label: 'SVG 1.1'}, {value: 'tiny-1.2', label: 'SVG Tiny 1.2'}].map((version) => (
              <label key={version.value} className="radio-label">
                <input
                  type="radio"
                  name="svgVersion"
                  value={version.value}
                  checked={options.svgVersion === version.value}
                  onChange={(e) => setOptions({...options, svgVersion: e.target.value as any})}
                />
                {version.label}
              </label>
            ))}
          </div>
        </div>

        <div className="option-section">
          <div className="section-title">
            SVG Options <span className="info-icon">ⓘ</span>
          </div>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.fixedSize}
              onChange={(e) => setOptions({...options, fixedSize: e.target.checked})}
            />
            Fixed Size
          </label>
          <label className="checkbox-label">
            <input
              type="checkbox"
              checked={options.adobeCompatibility}
              onChange={(e) => setOptions({...options, adobeCompatibility: e.target.checked})}
            />
            Adobe Compatibility Mode
          </label>
        </div>

        <div className="option-section">
          <div className="section-title">
            DXF Compatibility <span className="info-icon">ⓘ</span>
          </div>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="dxfCompatibility"
                value="lines-only"
                checked={options.dxfCompatibility === 'lines-only'}
                onChange={(e) => setOptions({...options, dxfCompatibility: e.target.value as any})}
              />
              Lines Only
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="dxfCompatibility"
                value="lines-arcs"
                checked={options.dxfCompatibility === 'lines-arcs'}
                onChange={(e) => setOptions({...options, dxfCompatibility: e.target.value as any})}
              />
              Lines & Arcs
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="dxfCompatibility"
                value="lines-arcs-splines"
                checked={options.dxfCompatibility === 'lines-arcs-splines'}
                onChange={(e) => setOptions({...options, dxfCompatibility: e.target.value as any})}
              />
              Lines, Arcs & Splines
            </label>
          </div>
        </div>

        <div className="option-section">
          <div className="section-title">
            Draw Style <span className="info-icon">ⓘ</span>
          </div>
          <div className="radio-group">
            <label className="radio-label">
              <input
                type="radio"
                name="drawStyle"
                value="fill"
                checked={options.drawStyle === 'fill'}
                onChange={(e) => setOptions({...options, drawStyle: e.target.value as any})}
              />
              Fill shapes
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="drawStyle"
                value="stroke-outline"
                checked={options.drawStyle === 'stroke-outline'}
                onChange={(e) => setOptions({...options, drawStyle: e.target.value as any})}
              />
              Stroke shape outlines
            </label>
            <label className="radio-label">
              <input
                type="radio"
                name="drawStyle"
                value="stroke-edges"
                checked={options.drawStyle === 'stroke-edges'}
                onChange={(e) => setOptions({...options, drawStyle: e.target.value as any})}
              />
              Stroke the edges between shapes once
            </label>
          </div>
        </div>

        <div className="options-row">
          <div className="option-section">
            <div className="section-title">
              Shape Stacking <span className="info-icon">ⓘ</span>
            </div>
            <div className="radio-group">
              <label className="radio-label">
                <input
                  type="radio"
                  name="shapeStacking"
                  value="cutouts"
                  checked={options.shapeStacking === 'cutouts'}
                  onChange={(e) => setOptions({...options, shapeStacking: e.target.value as any})}
                />
                Place shapes in cut-outs in shapes below
              </label>
              <label className="radio-label">
                <input
                  type="radio"
                  name="shapeStacking"
                  value="stacked"
                  checked={options.shapeStacking === 'stacked'}
                  onChange={(e) => setOptions({...options, shapeStacking: e.target.value as any})}
                />
                Stack shapes on top of each other
              </label>
            </div>

            <div className="section-title" style={{marginTop: '20px'}}>
              Group By <span className="info-icon">ⓘ</span>
            </div>
            <div className="radio-group">
              {['none', 'color', 'parent', 'layer'].map((group) => (
                <label key={group} className="radio-label">
                  <input
                    type="radio"
                    name="groupBy"
                    value={group}
                    checked={options.groupBy === group}
                    onChange={(e) => setOptions({...options, groupBy: e.target.value as any})}
                  />
                  {group.charAt(0).toUpperCase() + group.slice(1)}
                </label>
              ))}
            </div>

            <div className="section-title" style={{marginTop: '20px'}}>
              Parameterized Shapes <span className="info-icon">ⓘ</span>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.parameterizedShapes}
                onChange={(e) => setOptions({...options, parameterizedShapes: e.target.checked})}
              />
              Flatten
            </label>
          </div>

          <div className="option-section">
            <div className="section-title">
              Allowed Curve Types <span className="info-icon">ⓘ</span>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.allowedCurveTypes.lines}
                onChange={(e) => setOptions({...options, allowedCurveTypes: {...options.allowedCurveTypes, lines: e.target.checked}})}
              />
              Lines
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.allowedCurveTypes.quadratic}
                onChange={(e) => setOptions({...options, allowedCurveTypes: {...options.allowedCurveTypes, quadratic: e.target.checked}})}
              />
              Quadratic Bézier Curves
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.allowedCurveTypes.cubic}
                onChange={(e) => setOptions({...options, allowedCurveTypes: {...options.allowedCurveTypes, cubic: e.target.checked}})}
              />
              Cubic Bézier Curves
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.allowedCurveTypes.circular}
                onChange={(e) => setOptions({...options, allowedCurveTypes: {...options.allowedCurveTypes, circular: e.target.checked}})}
              />
              Circular Arcs
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.allowedCurveTypes.elliptical}
                onChange={(e) => setOptions({...options, allowedCurveTypes: {...options.allowedCurveTypes, elliptical: e.target.checked}})}
              />
              Elliptical Arcs
            </label>

            <div className="section-title" style={{marginTop: '20px'}}>
              Line Fit Tolerance <span className="info-icon">ⓘ</span>
            </div>
            <div className="radio-group">
              {['coarse', 'medium', 'fine', 'super-fine'].map((tolerance) => (
                <label key={tolerance} className="radio-label">
                  <input
                    type="radio"
                    name="lineFitTolerance"
                    value={tolerance}
                    checked={options.lineFitTolerance === tolerance}
                    onChange={(e) => setOptions({...options, lineFitTolerance: e.target.value as any})}
                  />
                  {tolerance.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
                </label>
              ))}
            </div>
          </div>

          <div className="option-section">
            <div className="section-title">
              Gap Filler <span className="info-icon">ⓘ</span>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.fillGaps}
                onChange={(e) => setOptions({...options, fillGaps: e.target.checked})}
              />
              Fill Gaps
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.clipOverflow}
                onChange={(e) => setOptions({...options, clipOverflow: e.target.checked})}
              />
              Clip Overflow
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.nonScalingStroke}
                onChange={(e) => setOptions({...options, nonScalingStroke: e.target.checked})}
              />
              Non-Scaling Stroke
            </label>

            <div className="slider-control">
              <label className="slider-label">
                <span className="slider-value">{options.strokeWidth.toFixed(1)}px</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="10"
                step="0.1"
                value={options.strokeWidth}
                onChange={(e) => setOptions({...options, strokeWidth: parseFloat(e.target.value)})}
                className="slider"
              />
            </div>

            <div className="section-title" style={{marginTop: '20px'}}>
              Stroke Style <span className="info-icon">ⓘ</span>
            </div>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.strokeStyle.nonScaling}
                onChange={(e) => setOptions({...options, strokeStyle: {...options.strokeStyle, nonScaling: e.target.checked}})}
              />
              Non-Scaling Stroke
            </label>
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={options.strokeStyle.overrideColor}
                onChange={(e) => setOptions({...options, strokeStyle: {...options.strokeStyle, overrideColor: e.target.checked}})}
              />
              Use Override Color
            </label>
            <label className="checkbox-label disabled">
              <input
                type="checkbox"
                checked={options.strokeStyle.fullColor}
                disabled
              />
              Full Color
            </label>

            <div className="slider-control">
              <label className="slider-label">
                <span className="slider-value">{options.strokeStyleWidth.toFixed(1)}px</span>
              </label>
              <input
                type="range"
                min="0.1"
                max="10"
                step="0.1"
                value={options.strokeStyleWidth}
                onChange={(e) => setOptions({...options, strokeStyleWidth: parseFloat(e.target.value)})}
                className="slider"
              />
            </div>
          </div>
        </div>

        <div className="action-buttons">
          <button className="download-btn-large" onClick={handleDownload}>
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" stroke="white" strokeWidth="2">
              <path d="M10 3V13M6 9L10 13L14 9"/>
              <path d="M4 17H16"/>
            </svg>
            DOWNLOAD
          </button>
          <button className="reset-btn" onClick={handleReset}>Reset</button>
        </div>
      </div>
    </div>
  );
};

export default Export;
